import { useEffect, useMemo, useState } from "react"
import api from "../../lib/api"
import { useVideoSuggestion } from "../../hooks/useVideoSuggestion"

export type PendingVideo = { id: string; title: string } | null

type SearchResult = {
  id: string
  title: string
  status: string
  lesson: { id: string; title: string } | null
}

type VideoAssociationPickerProps = {
  lessonTitle: string
  /** The lesson currently being edited — a result associated to THIS lesson is selectable, not disabled. `null` for a lesson not yet created. */
  currentLessonId: string | null
  value: PendingVideo
  onChange: (video: PendingVideo) => void
  /** Set after a 409 on submit — the pending video was taken by another lesson in the meantime. */
  fieldError?: string
}

const STATUS_BADGE: Record<string, string> = {
  ready: "Listo",
  processing: "Procesando",
  uploading: "Subiendo",
  error: "Error",
}

function IconFilm() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2.5" y="3" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.5 3v10M10.5 3v10" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  )
}

function IconSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function IconX() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

/**
 * Video picker for the lesson editor (video-manager-lesson-association-improvements,
 * decisiones 5-7): unlike ContentAssetPicker, this one never loads/shows the full
 * video library. It only shows a candidate when the lesson title suggests one, or
 * when the staff searches — and it never calls an association endpoint itself, it
 * only reports the pending selection up to the caller, applied on lesson save.
 */
export function VideoAssociationPicker({
  lessonTitle,
  currentLessonId,
  value,
  onChange,
  fieldError,
}: VideoAssociationPickerProps) {
  const { suggestion, loading: loadingSuggestion } = useVideoSuggestion(value ? "" : lessonTitle)
  const [searching, setSearching] = useState(false)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [showSearch, setShowSearch] = useState(false)

  useEffect(() => {
    if (!showSearch) return
    const q = query.trim()
    setSearching(true)
    const timer = setTimeout(async () => {
      try {
        const res = await api.get("/academy/admin/videos", {
          params: { search: q || undefined, status: "ready", limit: 8, page: 1 },
        })
        setResults(res.data?.data?.videos ?? [])
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [query, showSearch])

  const isTakenByAnotherLesson = useMemo(
    () => (video: SearchResult) => Boolean(video.lesson && video.lesson.id !== currentLessonId),
    [currentLessonId],
  )

  // Estado C — seleccionado, pendiente de guardar
  if (value) {
    return (
      <div>
        <span className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">Video</span>
        <div className="flex items-center gap-3 rounded-xl border border-brand-500 bg-brand-50 px-3.5 py-2.5 dark:border-brand-400 dark:bg-brand-500/10">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-brand-100 bg-white text-brand-600 dark:border-brand-500/30 dark:bg-gray-900">
            <IconFilm />
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800 dark:text-white">{value.title}</span>
          <button
            type="button"
            onClick={() => { onChange(null); setShowSearch(false); setQuery("") }}
            className="shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:bg-white/60 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            Cambiar
          </button>
        </div>
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Se asociará al guardar la lección.</p>
        {fieldError && <p className="mt-1.5 text-xs font-medium text-error-500">{fieldError}</p>}
      </div>
    )
  }

  return (
    <div>
      <span className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">Video</span>

      {/* Estado A — sugerencia automática por título de la lección */}
      {!showSearch && suggestion && (
        <div className="mb-2 flex items-center gap-3 rounded-xl border border-dashed border-brand-300 bg-brand-50/60 px-3.5 py-2.5 dark:border-brand-500/40 dark:bg-brand-500/5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-brand-100 bg-white text-brand-600 dark:border-brand-500/30 dark:bg-gray-900">
            <IconFilm />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] font-medium uppercase tracking-wide text-brand-500">Sugerido</span>
            <span className="block truncate text-sm text-gray-800 dark:text-white">{suggestion.title}</span>
          </span>
          <button
            type="button"
            onClick={() => onChange({ id: suggestion.id, title: suggestion.title })}
            className="shrink-0 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
          >
            Usar
          </button>
        </div>
      )}
      {!showSearch && !suggestion && loadingSuggestion && (
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Buscando un video sugerido...</p>
      )}

      {/* Estado B — búsqueda activa */}
      {!showSearch ? (
        <button
          type="button"
          onClick={() => setShowSearch(true)}
          className="flex h-9 w-full items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 text-sm text-gray-500 hover:border-gray-400 dark:border-gray-600 dark:text-gray-400"
        >
          <IconSearch />
          Buscar video...
        </button>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
              <IconSearch />
            </span>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por título..."
              className="h-9 w-full rounded-lg border border-gray-300 bg-white pl-8 pr-8 text-sm text-gray-800 focus:border-[var(--alas-blue)] focus:outline-none focus:ring-2 focus:ring-[var(--alas-blue)]/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
            />
            <button
              type="button"
              onClick={() => { setShowSearch(false); setQuery("") }}
              className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
            >
              <IconX />
            </button>
          </div>

          {searching ? (
            <p className="px-1 py-2 text-xs text-gray-500 dark:text-gray-400">Buscando...</p>
          ) : results.length === 0 ? (
            <p className="px-1 py-2 text-xs text-gray-500 dark:text-gray-400">
              {query.trim() ? `Sin resultados para "${query}".` : "No hay videos listos todavía."}
            </p>
          ) : (
            results.map((video) => {
              const taken = isTakenByAnotherLesson(video)
              return (
                <button
                  key={video.id}
                  type="button"
                  disabled={taken}
                  onClick={() => { onChange({ id: video.id, title: video.title }); setShowSearch(false); setQuery("") }}
                  className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors ${
                    taken
                      ? "cursor-not-allowed border-gray-200 opacity-50 dark:border-gray-700"
                      : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/60"
                  }`}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800">
                    <IconFilm />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-gray-800 dark:text-white">{video.title}</span>
                    {taken && (
                      <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                        Asociado a: {video.lesson?.title}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    {STATUS_BADGE[video.status] ?? video.status}
                  </span>
                </button>
              )
            })
          )}
        </div>
      )}

      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Sube nuevos videos desde Docentes → Videos y luego búscalos aquí.</p>
      {fieldError && <p className="mt-1.5 text-xs font-medium text-error-500">{fieldError}</p>}
    </div>
  )
}
